import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { useProgramClass } from '@/hooks/useProgramClass';
import { EntityType } from '@/models/EntityTypes';
import { courseService } from '@/services/CourseService';
import { momentWithTimeZone } from '@/services/UtilService';
import { isWorkshopConfigured, programHasWorkshopConflict } from '@/services/WorkshopService';
import { ApplicationState } from '@/store';
import React, { useMemo } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import './style.scss';

interface Props {
    programClassId: number;
}

const showErrorMessage = (message: string) => {
    return (
        <div className="class-card-error-msg">
            <i className="material-icons icon">{Constants.icons.REPORT_PROBLEM}</i>
            <span dangerouslySetInnerHTML={{ __html: message }}></span>
        </div>
    );
};

const ClassCard: React.FC<Props> = (props: Props) => {
    const strings = useSelector((state: ApplicationState) => state.locale.data);
    const programCourses = useSelector((state: ApplicationState) => state.program.courses);

    const { programClass, isLoading } = useProgramClass({
        programClassId: props.programClassId,
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const history = useHistory();

    const onEditClickHandler = () => {
        history.push(`${history.location.pathname}/${programClass?.id}`);
    };

    const getStartDate = useMemo(() => {
        const auxStartDate: Date | undefined = programClass?.startDate ? programClass.startDate : programClass?.scheduledStartDate;
        return auxStartDate ? momentWithTimeZone(auxStartDate, strings._LANGUAGE) : strings.UNDEFINED;
    }, [programClass?.startDate, programClass?.scheduledStartDate]);

    const getEndDate = useMemo(() => {
        return programClass?.finishDate ? momentWithTimeZone(programClass.finishDate, strings._LANGUAGE) : undefined;
    }, [programClass?.finishDate, strings._LANGUAGE]);

    const workshopType = useMemo(() => {
        if (programClass?.workshops && programClass.workshops.length) {
            return programClass.workshops[0].isOnline ? strings.WORKSHOP_TYPE_ONLINE : strings.WORKSHOP_TYPE_IN_PERSON;
        }
        return '';
    }, [programClass?.workshops, strings.WORKSHOP_TYPE_ONLINE, strings.WORKSHOP_TYPE_IN_PERSON]);

    const isProgram = programClass?.workshops && programClass.workshops.length > 1;

    const handleWorkshopConflicts = useMemo(() => {
        if (programClass?.workshops && programClass.workshops.length > 0) {
            return programHasWorkshopConflict(programClass.workshops, programCourses);
        }

        return false;
    }, [programClass?.workshops]);

    const getStartEndDateLabel: string = `${strings.START}: ${getStartDate}  ${getEndDate ? ' | ' + strings.END + ': ' + getEndDate : ''}`;

    const hasWorkshopNotConfigured = (): boolean =>
        programCourses.filter((c1) => c1.type === EntityType.WORKSHOP).some((course) => !isWorkshopConfigured(programClass?.workshops, course.id));

    const hasWorkshopConflicts: boolean = Boolean(programClass?.workshops && programClass.workshops.length > 0 && handleWorkshopConflicts);

    if (isLoading) return <Loader />;

    return (
        <div className="class-card">
            <div className="class-card-info">
                <span className="class-card-info__workshop-type">{workshopType}</span>
                <div className="class-card-info__header">
                    <div>
                        <h1 className="class-card-info__title">{programClass?.name || strings.CLASS_TITLE}</h1>
                        <span className="class-card-info__description__closed-classes">
                            {`${strings.CLOSED} | ${programClass?.enrollmentsCount || 0} ${
                                (programClass?.enrollmentsCount || 0) > 1 ? strings.ENROLLED_STUDENTS : strings.ENROLLED_STUDENT
                            }`}
                        </span>
                        <div className="class-card-info__description">
                            <div className="class-card-info__description__class-period">
                                <span>{`${getStartEndDateLabel}`}</span>
                            </div>
                        </div>
                    </div>
                    <div className="class-card-action">
                        <button className="btn btn-link btn--edit" onClick={onEditClickHandler}>
                            <span>{strings.EDIT}</span>
                            <i className="material-icons icon">{Constants.icons.EDIT}</i>
                        </button>
                    </div>
                </div>

                <div className="class-card-flex-row">
                    <div className="class-card-flex-column">
                        {programClass?.workshops && programClass.workshops.length > 0 && (
                            <div className="class-card-flex-column">
                                {isProgram ? (
                                    <span className="section-span-text strong">{strings.WORKSHOP_S}</span>
                                ) : (
                                    <span className="section-span-text">{strings.REALIZATION_OF_WORKSHOP}</span>
                                )}
                                {programClass.workshops.map((workshop) => (
                                    <div className="class-card-info__achievement-workshop" key={`${programClass.id}-${workshop.id}`}>
                                        {isProgram && (
                                            <span className="workshop-type">
                                                {workshop.isOnline ? strings.WORKSHOP_TYPE_ONLINE : strings.WORKSHOP_TYPE_IN_PERSON}
                                            </span>
                                        )}
                                        {workshop?.meetings?.map((meeting) => (
                                            <div className="class-card-info__description__class-period" key={meeting.id}>
                                                <span>
                                                    {courseService.getWorkshopDateWithEventPeriod(meeting.startDate, meeting.endDate, strings)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="d-flex flex-column w-50">
                        {hasWorkshopNotConfigured() && showErrorMessage(strings.WORKSHOP_NOT_CONFIGURED)}
                        {hasWorkshopConflicts && showErrorMessage(strings.WORKSHOPS_WITH_CONFLICT)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClassCard;
